import React from 'react';
import { Button, Card, Col, Dropdown, Row } from 'react-bootstrap';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import '../../form/CardForm.scss';
import { screeningClearanceRuleApi } from '../../apis';
import {
  CAN_ADD_SCREENING_CLEARANCE_RULE,
  CAN_DELETE_SCREENING_CLEARANCE_RULE,
  CAN_MODIFY_SCREENING_CLEARANCE_RULE,
} from '../../auth/permissions';
import Has from '../../auth/Has';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { DropdownWrapper } from '../../common/DropdownWrapper';
import {
  InternalApiScreeningClearanceRule,
  InternalApiScreeningClearanceRuleWithAllContents,
} from '../../api-internal';
import InfoSection from '../../form/InfoSection';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { parseErrorMessage } from '../../common/errors/helpers';

const validationSchema = Yup.object().shape(
  {
    reason: Yup.string().required('Required'),
    details: Yup.string().required('Required'),
  },
  []
);

export interface ScreeningClearanceRuleInfoFormValues {
  reason: string;
  details: string;
  enabled: boolean;
}

interface Props {
  rule: InternalApiScreeningClearanceRuleWithAllContents;
  editing: boolean;
  updateRule: any;
  setEditing: any;
}

export const ScreeningClearanceRuleInfo = ({ rule, updateRule, editing, setEditing }: Props): React.JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialValues: ScreeningClearanceRuleInfoFormValues = {
    reason: rule.reason,
    details: rule.details,
    enabled: rule.enabled,
  };

  const deleteRule = async (handle: string) => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        confirmationText: 'Yes, delete it!',
        cancelText: 'No, keep it',
        onConfirm: async () => {
          try {
            await screeningClearanceRuleApi.deleteRule({
              handle,
            });
            navigate('/screening/clearance-rules');
          } catch (_) {
            dispatch(
              showFeedback({
                message: 'Oops, something went wrong!',
                success: false,
              })
            );
          }
        },
      })
    );
  };

  const submit = async (
    formValues: ScreeningClearanceRuleInfoFormValues,
    { setStatus, setSubmitting }: FormikHelpers<ScreeningClearanceRuleInfoFormValues>
  ) => {
    try {
      const apiRule = {
        ...rule,
        reason: formValues.reason,
        details: formValues.details,
        enabled: formValues.enabled,
      };
      if (rule.handle) {
        await screeningClearanceRuleApi.updateRule({
          handle: rule.handle,
          internalApiScreeningClearanceRule: apiRule,
        });
        updateRule(apiRule);
      } else {
        const response: InternalApiScreeningClearanceRule = await screeningClearanceRuleApi.addRule({
          internalApiScreeningClearanceRule: apiRule,
        });
        navigate(`/screening/clearance-rules/${response.handle}`);
      }
    } catch (e: any) {
      const errorMessage = await parseErrorMessage(e);
      setStatus(errorMessage);
      dispatch(
        showFeedback({
          message: errorMessage,
          success: false,
        })
      );
    }
    setEditing(false);
    setSubmitting(false);
  };
  return (
    <Card body={false} className={'card-info'}>
      <Card.Header>
        <h4 className='card-header-title'>General</h4>
        {rule.handle && (
          <Row className='align-items-center'>
            <Has anyPermissions={[CAN_MODIFY_SCREENING_CLEARANCE_RULE, CAN_DELETE_SCREENING_CLEARANCE_RULE]}>
              {!editing && (
                <Col md={'auto'}>
                  <Button variant='white btn-sm' onClick={() => setEditing(true)}>
                    Edit general info
                  </Button>
                </Col>
              )}
              <Col>
                <DropdownWrapper>
                  <Dropdown.Toggle
                    data-testid='edit-dropdown'
                    as={Button}
                    className='btn-white btn-sm'
                    id='edit-dropdown'
                  >
                    •••
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={'p-0'}>
                    <Button
                      className='dropdown-item white btn-sm'
                      onClick={() => {
                        deleteRule(rule.handle as string);
                      }}
                    >
                      Delete
                    </Button>
                  </Dropdown.Menu>
                </DropdownWrapper>
              </Col>
            </Has>
          </Row>
        )}
      </Card.Header>
      <Card.Body>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values, resetForm, setFieldValue }: FormikProps<ScreeningClearanceRuleInfoFormValues>) => {
            return (
              <Form>
                <Row>
                  <Col md={5}>
                    <InfoSection
                      editing={editing}
                      label={'Reason'}
                      name={'reason'}
                      placeholder={'Enter rule title'}
                      value={values.reason}
                    />
                  </Col>
                  <Col md={1} />
                </Row>
                <Row>
                  <Col md={5}>
                    <InfoSection
                      editing={editing}
                      label={'Details'}
                      name={'details'}
                      type='textarea'
                      placeholder={'Enter rule details'}
                      value={values.details}
                    />
                    <Col md={1} />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    {rule.contents && rule.contents.length > 0 && (
                      <div className='editing-toggle-container'>
                        {editing ? (
                          <>
                            <label className={'form-label'}>Active</label>
                            <div className='form-check form-switch'>
                              <input
                                checked={values.enabled}
                                onChange={() => setFieldValue('enabled', !values.enabled)}
                                data-testid='enabledCheck'
                                type='checkbox'
                                className='form-check-input'
                                id={'enabledCheck'}
                              />
                              <label
                                className={`form-check-label ${editing ? 'active' : ''}`}
                                htmlFor={'enabledCheck'}
                              />
                            </div>
                          </>
                        ) : (
                          <label className={'form-label'}>{values.enabled ? 'Active' : 'Not active'}</label>
                        )}
                      </div>
                    )}
                  </Col>
                  <Col md={6} className='d-flex align-items-center justify-content-end'>
                    {editing && (
                      <Has anyPermissions={[CAN_ADD_SCREENING_CLEARANCE_RULE, CAN_MODIFY_SCREENING_CLEARANCE_RULE]}>
                        {rule.handle && (
                          // Show Cancel button only when editing
                          <Button
                            variant='outline-secondary'
                            onClick={() => {
                              resetForm();
                              setEditing(false);
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                        <Button type='submit' variant='primary' disabled={isSubmitting}>
                          Save
                        </Button>
                      </Has>
                    )}
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default ScreeningClearanceRuleInfo;
